/**
 * Offline support — cache plan, queue writes, sync when online.
 */

const KEYS = {
  PLAN: 'bodymax_offline_plan',
  PENDING_EXERCISES: 'bodymax_pending_exercises',
  PENDING_SESSIONS: 'bodymax_pending_sessions',
  BRAIN_STATE: 'bodymax_offline_brain',
};

// ═══ PLAN CACHE ═══
export function cachePlan(plan) {
  if (!plan) return;
  try {
    localStorage.setItem(KEYS.PLAN, JSON.stringify({ data: plan, ts: Date.now() }));
  } catch {}
}

export function getCachedPlan() {
  try {
    const raw = localStorage.getItem(KEYS.PLAN);
    if (!raw) return null;
    return JSON.parse(raw).data;
  } catch { return null; }
}

// ═══ BRAIN STATE CACHE ═══
export function cacheBrainState(bs) {
  if (!bs) return;
  try { localStorage.setItem(KEYS.BRAIN_STATE, JSON.stringify(bs)); } catch {}
}

export function getCachedBrainState() {
  try { return JSON.parse(localStorage.getItem(KEYS.BRAIN_STATE)); } catch { return null; }
}

// ═══ EXERCISE LOG QUEUE ═══
export function queueExerciseLog(log) {
  try {
    const pending = JSON.parse(localStorage.getItem(KEYS.PENDING_EXERCISES) || '[]');
    pending.push({ ...log, queued_at: new Date().toISOString() });
    localStorage.setItem(KEYS.PENDING_EXERCISES, JSON.stringify(pending));
  } catch {}
}

export function getPendingLogs() {
  try { return JSON.parse(localStorage.getItem(KEYS.PENDING_EXERCISES) || '[]'); } catch { return []; }
}

export function clearPendingLogs() {
  localStorage.removeItem(KEYS.PENDING_EXERCISES);
}

/**
 * Clear pending exercise logs for a specific session.
 * Called when a session is marked as 'completed' to prevent stale retries.
 */
export function clearPendingLogsForSession(sessionId) {
  if (!sessionId) return;
  try {
    const pending = getPendingLogs();
    const filtered = pending.filter(log => log.session_id !== sessionId);
    if (filtered.length > 0) {
      localStorage.setItem(KEYS.PENDING_EXERCISES, JSON.stringify(filtered));
    } else {
      clearPendingLogs();
    }
  } catch {}
}

// ═══ SESSION QUEUE (start/end) ═══
export function queueSession(type, data) {
  try {
    const pending = JSON.parse(localStorage.getItem(KEYS.PENDING_SESSIONS) || '[]');
    pending.push({ type, data, queued_at: new Date().toISOString() });
    localStorage.setItem(KEYS.PENDING_SESSIONS, JSON.stringify(pending));
  } catch {}
}

export function getPendingSessions() {
  try { return JSON.parse(localStorage.getItem(KEYS.PENDING_SESSIONS) || '[]'); } catch { return []; }
}

export function clearPendingSessions() {
  localStorage.removeItem(KEYS.PENDING_SESSIONS);
}

// ═══ SYNC ALL PENDING DATA ═══
export async function syncAll(api) {
  const results = { exercises: 0, sessions: 0, failed: 0 };
  
  // Sync exercise logs
  const exercises = getPendingLogs();
  const failedExercises = [];
  for (const log of exercises) {
    try {
      await api.post('/workout/complete-exercise', log);
      results.exercises++;
    } catch (err) {
      const status = err?.response?.status;
      // If session no longer active (404/410/400), discard instead of retrying
      if (status === 404 || status === 410 || (status === 400 && err?.response?.data?.detail?.includes?.('session'))) {
        // Stale item — session ended or doesn't exist, drop it
        results.exercises++; // Count as handled
      } else {
        failedExercises.push(log);
        results.failed++;
      }
    }
  }
  if (failedExercises.length > 0) {
    localStorage.setItem(KEYS.PENDING_EXERCISES, JSON.stringify(failedExercises));
  } else {
    clearPendingLogs();
  }
  
  // Sync sessions
  const sessions = getPendingSessions();
  const failedSessions = [];
  for (const s of sessions) {
    try {
      if (s.type === 'start') {
        await api.post('/workout/start-session', s.data);
      } else if (s.type === 'end') {
        await api.post('/workout/end-session', s.data);
      }
      results.sessions++;
    } catch (err) {
      const status = err?.response?.status;
      // If session no longer exists, discard
      if (status === 404 || status === 410) {
        results.sessions++;
      } else {
        failedSessions.push(s);
        results.failed++;
      }
    }
  }
  if (failedSessions.length > 0) {
    localStorage.setItem(KEYS.PENDING_SESSIONS, JSON.stringify(failedSessions));
  } else {
    clearPendingSessions();
  }
  
  return results;
}

// ═══ STATUS ═══
export function isOffline() {
  return !navigator.onLine;
}

export function getPendingCount() {
  return getPendingLogs().length + getPendingSessions().length;
}
